import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase, FileText, MessageSquare, DollarSign, CreditCard, CalendarDays, ClipboardList,
  Clock, AlertCircle, Upload, LogOut, User, Bot, Shield, X, Settings, ChevronDown
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import clientPortalService from '../../services/clientPortalService';
import usePortalWebSocket from '../../hooks/usePortalWebSocket';
import PortalNotifications from './PortalNotifications';
import useFirmInfo from '../../hooks/useFirmInfo';
import { AI_ASSISTANT_ENABLED } from '../../config/features';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const { firmName } = useFirmInfo();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const clientInfo = clientPortalService.getClientInfo();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real-time notifications
  const handleNotification = (notif) => {
    // Could show a toast here
    loadDashboard();
    loadOnboarding(); // Refresh stats on new notification
  };
  const { unreadCount, setUnreadCount } = usePortalWebSocket(handleNotification);

  useEffect(() => {
    loadDashboard();
    loadAnnouncements();
    loadOpenQuestionnaires();
  }, []);

  const [onboarding, setOnboarding] = useState(null);

  const loadOnboarding = async () => {
    try {
      const data = await clientPortalService.getOnboarding();
      const active = (data.onboarding || []).find(o => o.status === 'in_progress');
      setOnboarding(active || null);
    } catch (err) {
      // Ignore — feature may not be available
    }
  };

  // Open (in-progress / assigned) questionnaires — drives the resume banner.
  // A contact can have multiple open at once (intake + per-case forms), so this
  // is a list, not a single active item like onboarding.
  const [openQuestionnaires, setOpenQuestionnaires] = useState([]);

  const loadOpenQuestionnaires = async () => {
    try {
      const data = await clientPortalService.getQuestionnaires();
      const open = (data.questionnaires || []).filter(
        q => q.status === 'assigned' || q.status === 'in_progress'
      );
      setOpenQuestionnaires(open);
    } catch (err) {
      // Ignore — non-critical surface
    }
  };

  const [announcements, setAnnouncements] = useState([]);

  const loadAnnouncements = async () => {
    try {
      const data = await clientPortalService.getAnnouncements();
      setAnnouncements(data);
    } catch (err) {
      // Ignore — feature may not be available
    }
  };

  const dismissAnnouncement = async (id) => {
    try {
      await clientPortalService.dismissAnnouncement(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Failed to dismiss announcement:', err);
    }
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService.getDashboard();
      setDashboard(data);
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await clientPortalService.logout();
    navigate('/portal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-lg font-bold text-white leading-tight">{firmName || 'Client Portal'}</h1>
              <p className="text-sm text-virgo-teal-400">Welcome, {dashboard?.contact_name}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <PortalNotifications unreadCount={unreadCount} onCountChange={setUnreadCount} />
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-1 text-virgo-teal-400 hover:text-white transition-colors"
              >
                <User size={20} />
                <ChevronDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-virgo-navy-700 border border-virgo-teal-700/30 rounded-xl shadow-xl shadow-black/50 overflow-hidden z-50">
                  <Link
                    to="/portal/profile-setup"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 text-sm text-virgo-teal-200 hover:bg-virgo-navy-600 hover:text-white transition-colors"
                  >
                    <User size={16} />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/portal/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 text-sm text-virgo-teal-200 hover:bg-virgo-navy-600 hover:text-white transition-colors"
                  >
                    <Settings size={16} />
                    <span>Account Settings</span>
                  </Link>
                  <div className="border-t border-virgo-teal-700/30" />
                  <button
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-400 hover:bg-virgo-navy-600 hover:text-red-300 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Returning-client / forgot-password helper is login-only; a
            logged-in client is not locked out, so it does not belong here. */}

        {/* Resume open questionnaires — appears at the top, one row per open
            assignment. A contact can legitimately have an intake + per-case
            forms in flight, so this is a list, not a single CTA. */}
        {openQuestionnaires.length > 0 && (
          <div className="space-y-3 mb-6">
            {openQuestionnaires.map(q => {
              const lastSavedLabel = q.last_saved_at
                ? `Last saved ${formatDistanceToNow(parseISO(q.last_saved_at), { addSuffix: true })}`
                : 'Not started yet';
              return (
                <div
                  key={q.assignment_id}
                  className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex items-center gap-4"
                >
                  <div className="p-2 bg-amber-500/20 rounded-lg flex-shrink-0">
                    <ClipboardList className="text-amber-300" size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-amber-200 font-semibold text-sm truncate">
                      You have an unfinished form: {q.title}
                      {q.case_number && (
                        <span className="text-amber-300/70 font-normal ml-2">
                          ({q.case_number})
                        </span>
                      )}
                    </p>
                    <p className="text-amber-300/80 text-xs mt-1">{lastSavedLabel}</p>
                  </div>
                  <Link
                    to={`/portal/intake?assignment_id=${q.assignment_id}`}
                    className="px-4 py-2 bg-amber-500 text-virgo-navy-900 text-sm font-semibold rounded-lg hover:bg-amber-400 transition-colors flex-shrink-0"
                  >
                    Resume
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="space-y-3 mb-6">
            {announcements.map(ann => (
              <div key={ann.id} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-300">{ann.title}</p>
                  <p className="text-sm text-amber-200/80 mt-1">{ann.body}</p>
                </div>
                <button
                  onClick={() => dismissAnnouncement(ann.id)}
                  className="ml-4 text-amber-400/60 hover:text-amber-300 transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Briefcase className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Active Cases</p>
                <p className="text-2xl font-bold text-white">{dashboard?.active_cases || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <MessageSquare className="text-cyan-400" size={24} />
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Unread Messages</p>
                <p className="text-2xl font-bold text-white">{dashboard?.unread_messages || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <DollarSign className="text-amber-400" size={24} />
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Outstanding</p>
                <p className="text-2xl font-bold text-white">
                  ${(dashboard?.outstanding_balance || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
            <div className="flex items-center space-x-3">
              <div className={`p-2 ${dashboard?.overdue_balance > 0 ? 'bg-red-500/20' : 'bg-green-500/20'} rounded-lg`}>
                <AlertCircle className={dashboard?.overdue_balance > 0 ? 'text-red-400' : 'text-green-400'} size={24} />
              </div>
              <div>
                <p className="text-sm text-virgo-teal-400">Overdue</p>
                <p className={`text-2xl font-bold ${dashboard?.overdue_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  ${(dashboard?.overdue_balance || 0).toLocaleString()}
                </p>
                {dashboard?.overdue_balance > 0 && (
                  <Link to="/portal/invoices" className="text-xs text-red-300 hover:text-red-200 underline mt-1 inline-block">
                    Pay Now →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding Banner */}
        {onboarding && (
          <Link
            to={`/portal/onboarding/${onboarding.case_id}`}
            className="block bg-gradient-to-r from-emerald-600/30 via-cyan-500/20 to-emerald-600/30 rounded-xl border border-emerald-500/40 p-5 mb-6 hover:border-emerald-400/60 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <ClipboardList className="text-emerald-400" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Complete Your Setup</h3>
                  <p className="text-virgo-teal-300 text-sm">{onboarding.completed_steps}/{onboarding.total_steps} steps done</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 bg-virgo-navy-600 rounded-full h-2">
                  <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-2 rounded-full" style={{ width: `${onboarding.progress_percent}%` }} />
                </div>
                <span className="text-emerald-400 font-bold">{onboarding.progress_percent}%</span>
              </div>
            </div>
          </Link>
        )}

        {/* AI Assistant Banner — gated off for launch (see AI_ASSISTANT_ENABLED) */}
        {AI_ASSISTANT_ENABLED && (
        <Link
          to="/portal/ai"
          className="block bg-gradient-to-r from-cyan-600/30 via-blue-500/20 to-cyan-600/30 rounded-xl border border-cyan-500/40 p-6 mb-8 hover:border-cyan-400/60 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/astraea-avatar.png" alt="Astraea" className="w-14 h-14 rounded-xl object-cover group-hover:scale-110 transition-transform" />
              <div>
                <h3 className="text-xl font-bold text-white">Astraea AI Assistant</h3>
                <p className="text-virgo-teal-300">Ask questions about your cases, invoices, and documents</p>
              </div>
            </div>
            <span className="text-cyan-400 text-sm hidden md:block">Click to chat →</span>
          </div>
        </Link>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Link
            to="/portal/cases"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group"
          >
            <Briefcase className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">My Cases</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">View case details and updates</p>
          </Link>

          <Link
            to="/portal/messages"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group relative"
          >
            <MessageSquare className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Sigil</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">Communicate with your attorney</p>
            {dashboard?.unread_messages > 0 && (
              <span className="absolute top-4 right-4 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {dashboard.unread_messages}
              </span>
            )}
          </Link>

          {/* Visage "Coming Soon" tile removed — don't advertise a non-functional
              feature to clients. Restore when video conferencing actually ships. */}

          <Link
            to="/portal/invoices"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group"
          >
            <FileText className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Invoices</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">View and pay invoices</p>
          </Link>

          <Link
            to="/portal/documents"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group relative"
          >
            <Upload className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Upload Documents</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">Share files with your attorney</p>
            {dashboard?.pending_documents > 0 && (
              <span className="absolute top-4 right-4 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {dashboard.pending_documents}
              </span>
            )}
          </Link>

          <Link
            to="/portal/evidence"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group relative"
          >
            <Shield className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Evidence Requests</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">Review and sign consent forms</p>
            {dashboard?.pending_evidence_requests > 0 && (
              <span className="absolute top-4 right-4 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {dashboard.pending_evidence_requests}
              </span>
            )}
          </Link>

          <Link
            to="/portal/payments"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group"
          >
            <CreditCard className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Payments & Cards</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">Manage cards and payment plans</p>
          </Link>

          <Link
            to="/portal/appointments"
            className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group"
          >
            <CalendarDays className="text-cyan-400 mb-3 group-hover:scale-110 transition-transform" size={32} />
            <h3 className="text-white font-semibold">Appointments</h3>
            <p className="text-sm text-virgo-teal-400 mt-1">Schedule or manage appointments</p>
          </Link>
        </div>

        {/* My Calendar Quick Link */}
        <Link
          to="/portal/calendar"
          className="block bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-4 mb-8 hover:border-cyan-500/50 transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalendarDays className="text-cyan-400" size={24} />
              <div>
                <h3 className="text-white font-semibold">My Calendar</h3>
                <p className="text-sm text-virgo-teal-400">View all upcoming events across your matters</p>
              </div>
            </div>
            <span className="text-cyan-400 text-sm hidden md:block">View all &rarr;</span>
          </div>
        </Link>

        {/* Upcoming Dates */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <CalendarDays className="text-cyan-400" size={20} />
            <h2 className="text-lg font-bold text-white">Upcoming Dates</h2>
          </div>
          {dashboard?.upcoming_deadlines?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.upcoming_deadlines.map((event, idx) => {
                const eventDate = new Date(event.start_time);
                const now = new Date();
                const diffDays = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
                const isToday = diffDays <= 0 && diffDays > -1;
                const isTomorrow = diffDays === 1;
                const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                const timeLabel = event.all_day ? 'All day' : eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div key={event.event_id || idx} className="flex items-center space-x-4 p-3 bg-virgo-navy-800/50 rounded-lg">
                    <div className="text-center min-w-[48px]">
                      <p className={`text-xs font-semibold ${isToday ? 'text-amber-400' : isTomorrow ? 'text-cyan-400' : 'text-virgo-teal-400'}`}>
                        {dateLabel}
                      </p>
                      <p className="text-xs text-virgo-teal-500">{timeLabel}</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{event.title}</p>
                      {event.case_name && (
                        <span className="inline-block text-xs text-virgo-teal-400 bg-virgo-navy-700 px-2 py-0.5 rounded mt-1">
                          {event.case_name}
                        </span>
                      )}
                    </div>
                    {event.visage_meeting_link && (
                      <a href={event.visage_meeting_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 text-xs">
                        Join
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CalendarDays className="text-virgo-teal-600 mx-auto mb-2" size={32} />
              <p className="text-virgo-teal-500">No upcoming dates in the next 30 days</p>
              <Link to="/portal/calendar" className="text-cyan-400 hover:text-cyan-300 text-sm mt-2 inline-block">
                View my calendar →
              </Link>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
          <h2 className="text-lg font-bold text-white mb-4">Recent Activity</h2>

          {dashboard?.recent_activity?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_activity.map((activity, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-3 p-3 rounded-lg ${
                    activity.is_unread ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-virgo-navy-800/50'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    activity.activity_type === 'message' ? 'bg-cyan-500/20' :
                    activity.activity_type === 'invoice' ? 'bg-amber-500/20' :
                    activity.activity_type === 'document' ? 'bg-blue-500/20' : 'bg-green-500/20'
                  }`}>
                    {activity.activity_type === 'message' && <MessageSquare size={16} className="text-cyan-400" />}
                    {activity.activity_type === 'invoice' && <DollarSign size={16} className="text-amber-400" />}
                    {activity.activity_type === 'document' && <FileText size={16} className="text-blue-400" />}
                    {activity.activity_type === 'update' && <Clock size={16} className="text-green-400" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{activity.title}</p>
                    <p className="text-sm text-virgo-teal-400">{activity.description}</p>
                    {activity.case_name && (
                      <p className="text-xs text-virgo-teal-500 mt-1">{activity.case_name}</p>
                    )}
                  </div>
                  <span className="text-xs text-virgo-teal-500">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-virgo-teal-500 text-center py-8">No recent activity</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default PortalDashboard;
